import React, { useRef } from "react";
import useEditorPopupStyles from "./themes";

import useDashboardEditor from "MyDashboards/state/editor";
import { Element } from "MyDashboards/state/editorTypes";
import { Rnd } from "react-rnd";
import { useClickAway } from "react-use";
import { ReactComponent as DragHandle } from "../../assets/DragHandle.svg";
import { TDashboardComponentRegistry } from "../ComponentRegistry/types";
import * as reg from "../Render";
import TabsBar from "./TabsBar";
import { TEditorMode, TTabOptionType } from "./types";
import { t } from "i18next";
type Props = {
  registry?: TDashboardComponentRegistry;
};

const TEXT_OPTIONS: TTabOptionType[] = [
  { title: "Text", value: "text" },
  { title: "Metadata", value: "metadata" },
];
const IMAGE_OPTIONS: TTabOptionType[] = [
  { title: "Chart", value: "chart" },
  { title: "Image", value: "image" },
];

const EditorPopup = (props: Props) => {
  const wrapperRef = useRef(null);
  const registry = props.registry || reg.fullScreen;
  const setEditAction = useDashboardEditor((s) => s.setEditAction);
  const updateSelectedElement = useDashboardEditor(
    (s) => s.setSelectedElementProperties
  );

  useClickAway(wrapperRef, () => {
    setEditAction(undefined);
  });
  const classes = useEditorPopupStyles();
  const selectedElement = useDashboardEditor((s) =>
    s.pages
      .find((p) => p.id === s.selectedPageId)
      ?.elements.find((el) => el.id === s.selectedElementId)
  );
  const elementTargetType = !selectedElement
    ? ""
    : selectedElement?.paragraphs && selectedElement.paragraphs.length > 0
    ? "Text element"
    : "Image element";
  const options =
    elementTargetType === "Text element" ? TEXT_OPTIONS : IMAGE_OPTIONS;

  const [editorState, setEditorState] = React.useState<TEditorMode>(() => {
    if (selectedElement?.type === "image") {
      if (selectedElement.component) {
        return "chart";
      }
      return "image";
    }
    if (selectedElement?.component?.component === "metadata") return "metadata";
    return options[0].value;
  });

  const cachedComponents = useRef<Record<string, Element | undefined>>({});
  const prevEditorState = useRef<TEditorMode>("");

  React.useEffect(() => {
    // Check if the state has changed at all
    if (
      prevEditorState.current === editorState ||
      prevEditorState.current === ""
    ) {
      cachedComponents.current[editorState] = selectedElement;
      cachedComponents.current.__last = selectedElement;
      prevEditorState.current = editorState;
      return;
    }

    const getDerivedState = (state: TEditorMode): Partial<Element> => {
      const lastComponent = cachedComponents.current[state];
      if (state === "image") {
        if (lastComponent) {
          return {
            ...lastComponent,
            component: null,
          };
        }
        return {
          component: null,
          rendered: {},
        };
      }

      if (state === "chart") {
        if (lastComponent) {
          return {
            ...lastComponent,
            rendered: {},
          };
        }
      }

      if (state === "metadata") {
        const lastTextComponent = cachedComponents.current.text;
        if (lastTextComponent) {
          const lastPara = lastTextComponent?.paragraphs?.[0];
          if (lastComponent && lastPara) {
            return {
              ...lastComponent,
              paragraphs: [
                lastPara,
                {
                  portions: [],
                  textAlign: "",
                  rtl: false,
                },
              ],
              rendered: {},
            };
          }
        }
        if (lastComponent) {
          return {
            ...lastComponent,
            rendered: {},
          };
        }
      }

      if (state === "text") {
        const lastMetaComponent = cachedComponents.current.metadata;
        console.log("Last meta component", lastMetaComponent);
        if (lastMetaComponent) {
          return {
            ...lastMetaComponent,
            component: {
              component: editorState.toLocaleLowerCase(),
              data: {},
              tag: "COMPONENT",
              width: 500,
              height: 300,
            },
            rendered: {},
          };
        }
      }
      return {
        component: {
          component: editorState.toLocaleLowerCase(),
          data: {},
          tag: "COMPONENT",
          width: 500,
          height: 300,
        },
        rendered: {},
      };
    };
    const newComponent = getDerivedState(editorState);
    updateSelectedElement(newComponent);

    // detect change in the editor state
    prevEditorState.current = editorState;

    // cache the active selection as "last" version of some type
    cachedComponents.current[editorState] = selectedElement;
  }, [editorState, selectedElement]);

  return (
    <Rnd
      style={{ zIndex: 999 }}
      dragHandleClassName={classes.header}
      className={classes.defaultDrag}
    >
      <div className={classes.popup} ref={wrapperRef}>
        <div className={classes.header}>
          <DragHandle />
        </div>
        <div>
          <TabsBar
            options={options}
            handleChange={(value) => setEditorState(value)}
            initialMode={editorState}
          />
        </div>
        {selectedElement ? (
          <registry.Edit
            el={selectedElement}
            onChange={(newData) => {
              updateSelectedElement(newData);
            }}
          />
        ) : null}
      </div>
    </Rnd>
  );
};

export default EditorPopup;
